import { useState } from 'react';

/**
 * Sprint 7 — Tabbed editor avec compteurs intégrés (pattern Float §3.1).
 *
 * Props :
 *  - tabs       : Array<{ key, label, count?, badge?, content }>
 *  - defaultTab : key du tab actif initial (défaut : premier tab)
 *  - activeKey  : (optionnel) tab actif contrôlé de l'extérieur
 *  - onChange   : (key) => void
 *
 * Le compteur s'affiche **collé** au label (style Float : `Team1`, `Phases0`)
 * et s'atténue à 0.3 si === 0 pour qu'on voie d'un coup d'œil les sections
 * vides sans cliquer dessus.
 */
export default function TabbedEditor({ tabs, defaultTab, activeKey, onChange }) {
  const [internalActive, setInternalActive] = useState(defaultTab ?? tabs[0]?.key);
  const active = activeKey ?? internalActive;

  function select(key) {
    if (activeKey === undefined) setInternalActive(key);
    onChange?.(key);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <div
        role="tablist"
        style={{
          display: 'flex',
          gap: 4,
          borderBottom: '1px solid var(--glass-border)',
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        {tabs.map((tab) => {
          const isActive = active === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => select(tab.key)}
              style={{
                background: 'transparent',
                border: 'none',
                borderBottom: isActive
                  ? '2px solid var(--accent)'
                  : '2px solid transparent',
                color: isActive ? 'var(--accent)' : 'var(--text-muted)',
                padding: '8px 12px',
                fontWeight: isActive ? 600 : 500,
                fontSize: 14,
                cursor: 'pointer',
                marginBottom: -1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0,
              }}
            >
              {tab.label}
              {tab.count !== undefined && tab.count !== null && (
                <span
                  data-empty={tab.count === 0}
                  style={{
                    marginLeft: 4,
                    fontSize: '0.85em',
                    opacity: tab.count === 0 ? 0.3 : 0.75,
                    fontWeight: 500,
                  }}
                >
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        {tabs.map((tab) => (
          <div
            key={tab.key}
            role="tabpanel"
            hidden={active !== tab.key}
            style={active === tab.key ? { display: 'block' } : undefined}
          >
            {active === tab.key && tab.content}
          </div>
        ))}
      </div>
    </div>
  );
}
